import React, { useEffect, Suspense } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Books from "@/pages/Books";
import BookDetail from "@/pages/BookDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import { ConfirmSubscribe, Unsubscribe } from "@/pages/MailState";
import PaymentSuccess, { PaymentCancel } from "@/pages/Payment";
import News from "@/pages/News";
import NewsPost from "@/pages/NewsPost";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#070A11] px-6 text-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF]">Something broke</p>
            <p className="mt-4 font-serif text-2xl text-[#F1F5F9]">Please refresh the page.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Site = () => (
  <>
    <ScrollToTop />
    <Nav />
    <main>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/books" element={<Books />} />
        <Route path="/books/:slug" element={<BookDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:slug" element={<NewsPost />} />
        <Route path="/subscribe/confirm" element={<ConfirmSubscribe />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
      </Routes>
    </main>
    <Footer />
  </>
);

function App() {
  useEffect(() => {
    let lenis;
    let rafId;
    let cancelled = false;
    (async () => {
      const Lenis = (await import("lenis")).default;
      if (cancelled) return;
      lenis = new Lenis({ lerp: 0.09 });
      const raf = (t) => {
        lenis.raf(t);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    })();
    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (lenis) lenis.destroy();
    };
  }, []);

  return (
    <ErrorBoundary>
      <div className="relative min-h-screen bg-[#070A11] text-[#F1F5F9]">
        <div className="grain-overlay" aria-hidden />
        <BrowserRouter>
          <Suspense fallback={null}>
            <Site />
          </Suspense>
        </BrowserRouter>
        <Toaster
          theme="dark"
          toastOptions={{
            style: {
              background: "#0E1422",
              border: "1px solid rgba(0,240,255,0.25)",
              color: "#F1F5F9",
            },
          }}
        />
      </div>
    </ErrorBoundary>
  );
}

export default App;
